// Reactive auth + tier state for the whole app. This is the ONE place the rest of the app reads
// "who is signed in and what can they unlock". It exposes a plain snapshot { user, session, tier,
// profile, isPremium, isAdmin } and a subscribe() — no framework, same declarative style as the
// rest of src/.
//
// FREEMIUM: signed-out is the default and fully functional. tier defaults to 'free'; nothing here
// gates the chart. Later work reads isPremium / isAdmin to UNLOCK premium tiles + social — it must
// never use the signed-out state to lock the base app.
//
// OFFLINE-FIRST: after a successful sign-in we read the tier from public.accounts and CACHE it
// (keyed by user id) in localStorage, alongside the persisted session (see ./client). On the next
// cold start with no network, the cached session restores and we re-apply the cached tier
// immediately, so premium stays unlocked offline. The network read, when it succeeds, refreshes
// the cache. A read of the tier or the profile that fails in transit (offline, 5xx, rate limit) is
// asked again on a backoff and at once when the network or the page comes back — on a device with no
// cached tier yet, a failed read would otherwise leave the depth chart, HUD and grounding alarm
// locked as "needs Premium" (and the account show no name or boat) until the next auth event (the
// hourly token refresh).
//
// A SESSION LOST WHILE THE APP RUNS IS NOT A SIGN-OUT. Only the in-app Sign out (this device only)
// resets to the wall. When the server rejects the refresh token instead — signed out on another
// device, session expired — the running app keeps everything it has (chart, tier, anchor watch,
// alarms) and `sessionLost` asks the user to sign back in; the boat may be offline for days.

import { isAuthRetryableFetchError, type Session, type User } from '@supabase/supabase-js';
import { AUTH_STORAGE_KEY, authStorage, supabase } from './client';
import { backoffMs } from '../data/backoff';

export type Tier = 'free' | 'premium' | 'admin';

/** public.profiles row (readable for all authenticated users per RLS). */
export interface Profile {
  username: string | null;
  display_name: string | null;
  boat_name: string | null;
  avatar_url: string | null;
  bio: string | null;
}

export interface AuthState {
  user: User | null;
  session: Session | null;
  tier: Tier;
  profile: Profile | null;
  isPremium: boolean; // premium OR admin — the gate later work checks for premium features
  isAdmin: boolean;
  /** The session ended while the app was running WITHOUT an in-app sign-out: the refresh token was
   *  rejected (signed out on another device, or expired). user/session are gone; the tier the
   *  running app already had stays until a sign-in or reload, so nothing loaded is torn down. */
  sessionLost: boolean;
}

/** Result of an auth action, shaped for direct display in the login panel. */
export interface AuthResult {
  ok: boolean;
  message?: string;
}

type Listener = (s: AuthState) => void;

const TIER_VALUES: readonly Tier[] = ['free', 'premium', 'admin'];
const isTier = (v: unknown): v is Tier => typeof v === 'string' && (TIER_VALUES as readonly string[]).includes(v);

// ── offline tier/profile cache (keyed by user id so a different user can't inherit a stale tier) ──
const CACHE_KEY = 'seabearing:auth:cache';
interface Cache {
  userId: string;
  tier: Tier;
  profile: Profile | null;
}
function loadCache(): Cache | null {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw) as Cache;
    if (typeof c?.userId === 'string' && isTier(c.tier)) return c;
  } catch {
    /* unavailable / corrupt — treat as no cache */
  }
  return null;
}
function saveCache(c: Cache): void {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(c));
  } catch {
    /* private mode — premium just won't persist offline; never fatal */
  }
}
function clearCache(): void {
  try {
    window.localStorage.removeItem(CACHE_KEY);
  } catch {
    /* ignore */
  }
}

/** The session as persisted by the auth client (./client), read straight from storage — no network.
 *  auth-js keeps it stored while a refresh fails for a transient reason and deletes it only when the
 *  server rejects the refresh token, so "stored" is the truth about whether this device is signed in. */
function readStoredSession(): Session | null {
  try {
    const raw = authStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Partial<Session> | null;
    if (s && typeof s.access_token === 'string' && typeof s.refresh_token === 'string' && typeof s.user?.id === 'string') {
      return s as Session;
    }
  } catch {
    /* unreadable / corrupt — not a session */
  }
  return null;
}

/** Redirect target for magic-link / OAuth: back to the current page, minus any query/fragment. */
function redirectTo(): string {
  const { origin, pathname } = window.location;
  return origin + pathname;
}

/** A sign-in error in plain words (ui/jargonGuard.ts): the auth server's own sentences for people
 *  (wrong password, unconfirmed email, the invite key's refusal) pass; anything else (a database or a
 *  provider error) is a log detail. */
const friendly = (e: { message?: string } | null | undefined): string => {
  if (isAuthRetryableFetchError(e)) return 'The sign-in server can’t be reached. Check the connection and try again.';
  const m = e?.message?.trim() ?? '';
  if (/invalid login credentials/i.test(m)) return 'Wrong email or password.';
  if (/email not confirmed/i.test(m)) return 'Confirm your email first: open the link we sent you.';
  if (/already registered|already been registered/i.test(m)) return 'There is already an account with that email. Sign in instead.';
  if (/rate limit|too many/i.test(m)) return 'Too many tries. Wait a few minutes and try again.';
  if (/password should be|weak password/i.test(m)) return m;
  if (/invite/i.test(m)) return m; // the invite trigger's refusal is written for people
  if (m) console.warn('sign-in:', m);
  return 'Sign-in didn’t work. Try again.';
};

/** A REST answer that says nothing about the account: no response (status 0 — postgrest-js reports
 *  a network failure that way), a timeout, a rate limit or a server error. Any other answer — the row,
 *  no row, a refused token — is the server's word, and asking again would not change it. */
const inTransit = (status: number): boolean => status === 0 || status === 408 || status === 429 || status >= 500;
// The account read's retry backoff: 5 s doubling to 5 min.
const ACCOUNT_RETRY_BASE_MS = 5000;
const ACCOUNT_RETRY_CAP_MS = 300_000;

class AuthStore {
  private state: AuthState = {
    user: null, session: null, tier: 'free', profile: null, isPremium: false, isAdmin: false, sessionLost: false,
  };
  private listeners = new Set<Listener>();
  private signingOut = false; // an in-app signOut() is in flight: its SIGNED_OUT is deliberate
  // The account read owed after one failed in transit: for whom, failures in a row, the backoff timer.
  private accountRetry: { userId: string; fails: number; timer: ReturnType<typeof setTimeout> } | null = null;

  // Resolves once the INITIAL session has been resolved from storage (offline-first: no network
  // needed) — i.e. we KNOW whether the returning user is signed in. The auth gate waits on this
  // before deciding wall-vs-app, so it never flashes the login form ahead of a cached session.
  private markReady!: () => void;
  private isReady = false;
  readonly ready: Promise<void> = new Promise<void>((res) => { this.markReady = res; });

  constructor() {
    // Decide from the stored session first. The auth client reports only after its initialize,
    // which refreshes an expired access token over the network first: offline that is ~25 s of
    // retries (or a fetch that hangs on a dead marina link), and it then reports NO session even
    // though the session is still stored (GoTrueClient __loadSession → INITIAL_SESSION null).
    const stored = readStoredSession();
    if (stored) {
      this.applySession(stored);
      this.setReady();
    }
    // The single subscription. supabase fires INITIAL_SESSION on subscribe with the restored
    // session (or null), then SIGNED_IN / SIGNED_OUT / TOKEN_REFRESHED / USER_UPDATED as they
    // happen. detectSessionInUrl also lands the post-redirect session through here.
    supabase.auth.onAuthStateChange((_event, session) => {
      if (session) this.applySession(session);
      else this.applyNoSession();
      this.setReady();
    });
    // Safety net: if the auth client never reports (init error), unblock the gate so the user can
    // at least see the login wall rather than an endless splash.
    setTimeout(() => this.setReady(), 8000);
    // An owed account read goes out as soon as the network or the page is back, not at its backoff.
    window.addEventListener('online', () => this.retryAccountNow());
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') this.retryAccountNow(); });
  }

  private setReady(): void {
    if (this.isReady) return;
    this.isReady = true;
    this.markReady();
  }

  /** Current snapshot. */
  get snapshot(): AuthState {
    return this.state;
  }

  // Convenience reads of the current tier gate (always reflect the latest applied state).
  get isPremium(): boolean { return this.state.isPremium; }
  get isAdmin(): boolean { return this.state.isAdmin; }
  get tier(): Tier { return this.state.tier; }

  /** Subscribe to state changes. Fires immediately with the current state. Returns an unsubscribe. */
  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.state);
    return () => this.listeners.delete(fn);
  }

  // ── auth actions (each returns a display-ready result; nothing throws to the caller) ──

  async signUpWithEmail(email: string, password: string, inviteKey: string): Promise<AuthResult> {
    // Seabearing is invite-only: a DB trigger on auth.users claims the (single-use) key named in
    // the signup metadata and aborts the signup without a valid one. The trigger's message passes
    // through GoTrue verbatim, so a bad key still fails with readable copy.
    const key = inviteKey.trim();
    if (!key) return { ok: false, message: 'An invite key is required — Seabearing is invite-only for now.' };
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { invite_key: key }, emailRedirectTo: redirectTo() },
    });
    if (error) return { ok: false, message: friendly(error) };
    // A signup trigger creates the account+profile server-side. If email confirmation is on,
    // there's no session yet — tell the user to confirm.
    if (!data.session) return { ok: true, message: 'Check your email to confirm your account.' };
    return { ok: true };
  }

  async signInWithEmail(email: string, password: string): Promise<AuthResult> {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { ok: false, message: friendly(error) };
    return { ok: true };
  }

  /** Passwordless OTP / magic-link sign-in for EXISTING accounts. Creation goes through the
   *  invite-key signup (shouldCreateUser would trip the invite trigger with an opaque error). */
  async signInWithMagicLink(email: string): Promise<AuthResult> {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: redirectTo(), shouldCreateUser: false },
    });
    if (error) {
      const msg = /signups not allowed/i.test(error.message ?? '')
        ? 'No account with that email yet — create one with your invite key.'
        : friendly(error);
      return { ok: false, message: msg };
    }
    return { ok: true, message: 'Check your email for the sign-in link.' };
  }

  /**
   * OAuth via Google / Apple. These providers need to be enabled + configured in the Supabase
   * console (the founder's task: client IDs/secrets + this app's redirect URL). Until then the
   * call returns a "provider is not enabled" error, which the login panel surfaces — so the
   * buttons effectively no-op cleanly rather than break.
   */
  async signInWithOAuth(provider: 'google' | 'apple'): Promise<AuthResult> {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: redirectTo() },
    });
    // On success the browser navigates to the provider, so we never reach the resolve with data.
    if (error) return { ok: false, message: friendly(error) };
    return { ok: true };
  }

  /** Sign out THIS DEVICE only. supabase-js defaults to scope 'global', which revokes every session
   *  of the user — the boat phone's next token refresh would then fail. */
  async signOut(): Promise<AuthResult> {
    this.signingOut = true;
    try {
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      // On failure (e.g. offline) the session and its cached tier stay: still signed in.
      if (error) return { ok: false, message: friendly(error) };
      return { ok: true };
    } finally {
      this.signingOut = false;
    }
  }

  // ── internal ──

  private applySession(session: Session): void {
    const user = session.user;
    // Restore the cached tier/profile for THIS user immediately (offline-ready), then refresh
    // from the network in the background.
    const cached = loadCache();
    const tier: Tier = cached && cached.userId === user.id ? cached.tier : 'free';
    const profile = cached && cached.userId === user.id ? cached.profile : null;
    this.set({ user, session, tier, profile, sessionLost: false });
    void this.refreshFromNetwork(user.id);
  }

  /** The auth client reports no session. Three different things: */
  private applyNoSession(): void {
    // (1) Still stored → the client only failed to refresh in transit (offline, 5xx) and kept the
    //     session (GoTrueClient _callRefreshToken removes it only on a non-retryable error), but it
    //     reports null for an expired access token. Still signed in; the refresh retries.
    const stored = readStoredSession();
    if (stored) {
      if (!this.state.user) this.applySession(stored);
      return;
    }
    // (2) The in-app Sign out: back to anonymous free, cached tier dropped.
    if (this.signingOut) {
      clearCache();
      this.set({ user: null, session: null, tier: 'free', profile: null, sessionLost: false });
      return;
    }
    // (3) Involuntary: the server rejected the refresh token (signed out elsewhere, expired), or
    //     another tab/surface cleared the session. Keep the tier the running app has — dropping it
    //     would switch off the depth chart, HUD depth and grounding alarm — and flag the loss.
    if (this.state.user || this.state.sessionLost) {
      this.set({ user: null, session: null, sessionLost: true });
      return;
    }
    // Nobody was signed in on this page load: anonymous (the auth gate shows the wall).
    this.set({ user: null, session: null, tier: 'free', profile: null });
  }

  /** Read tier from public.accounts + the profile, then update state + offline cache. A read of
   *  either that fails in transit keeps its cached value and both are asked again
   *  (scheduleAccountRetry) until the server answers; only a tier the server returned is cached. */
  private async refreshFromNetwork(userId: string): Promise<void> {
    if (this.accountRetry?.userId === userId) clearTimeout(this.accountRetry.timer); // this read is the retry
    let transit = true;
    try {
      const [acct, prof] = await Promise.all([
        supabase.from('accounts').select('tier').eq('id', userId).single(),
        supabase.from('profiles').select('username,display_name,boat_name,avatar_url,bio').eq('id', userId).single(),
      ]);
      // A guard against a late response for a user we've since signed out of / switched away from.
      if (this.state.user?.id !== userId) return;
      transit = (!!acct.error && inTransit(acct.status)) || (!!prof.error && inTransit(prof.status));

      const read = !acct.error && isTier(acct.data?.tier);
      const tier: Tier = read ? (acct.data.tier as Tier) : this.state.tier;
      const profile: Profile | null = !prof.error && prof.data ? (prof.data as Profile) : this.state.profile;
      this.set({ tier, profile });
      if (read) saveCache({ userId, tier, profile });
    } catch {
      /* offline / network error — keep the cached tier; premium stays as it was (retried below) */
      if (this.state.user?.id !== userId) return;
    }
    if (transit) this.scheduleAccountRetry(userId);
    else if (this.accountRetry?.userId === userId) this.accountRetry = null; // answered: nothing owed
  }

  /** The account read failed in transit: ask again on the backoff (and on online / visible). */
  private scheduleAccountRetry(userId: string): void {
    const fails = this.accountRetry?.userId === userId ? this.accountRetry.fails + 1 : 1;
    if (this.accountRetry) clearTimeout(this.accountRetry.timer);
    const ms = backoffMs(fails, ACCOUNT_RETRY_BASE_MS, ACCOUNT_RETRY_CAP_MS);
    this.accountRetry = { userId, fails, timer: setTimeout(() => this.retryAccountNow(), ms) };
  }

  /** Send the owed account read now, if one is owed for the user still signed in. */
  private retryAccountNow(): void {
    const r = this.accountRetry;
    if (!r) return;
    if (this.state.user?.id !== r.userId) {
      clearTimeout(r.timer);
      this.accountRetry = null;
      return;
    }
    void this.refreshFromNetwork(r.userId);
  }

  private set(patch: Partial<AuthState>): void {
    const next: AuthState = { ...this.state, ...patch };
    next.isAdmin = next.tier === 'admin';
    next.isPremium = next.tier === 'premium' || next.tier === 'admin';
    this.state = next;
    for (const fn of this.listeners) fn(this.state);
  }
}

/** The app-wide auth store. Import this anywhere that needs auth/tier state. */
export const auth = new AuthStore();
